import React, { useState, useEffect } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Small pill badge that shows CHANTIX sync status.
 *
 * Props:
 *  - sync: {sync_at: string, stale: boolean, error?: string} | null
 *  - onRefresh: () => Promise<void>   // called when user taps the refresh icon
 *  - loading: boolean                 // parent-controlled loading state
 */
export default function SyncBadge({ sync, onRefresh, loading = false }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // Re-render every 30s so the "il y a X" label stays fresh
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  if (!sync?.sync_at && !loading) return null;

  const syncAt = sync?.sync_at ? new Date(sync.sync_at) : null;
  const stale = !!sync?.stale;
  const now = new Date();
  const secondsAgo = syncAt ? Math.floor((now - syncAt) / 1000) : 0;

  const relative = (() => {
    if (secondsAgo < 15) return "à l'instant";
    if (secondsAgo < 60) return `il y a ${secondsAgo}s`;
    if (secondsAgo < 3600) return `il y a ${Math.floor(secondsAgo / 60)}min`;
    if (secondsAgo < 86400) return `il y a ${Math.floor(secondsAgo / 3600)}h`;
    return `il y a ${Math.floor(secondsAgo / 86400)}j`;
  })();

  const absolute = syncAt
    ? syncAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "—";

  const Icon = loading ? Loader2 : stale ? AlertTriangle : CheckCircle2;
  const colorCls = stale ? "text-amber-400" : "text-emerald-400";
  const bgCls = stale ? "bg-amber-500/10 border-amber-500/30" : "bg-emerald-500/5 border-emerald-500/20";

  return (
    <div
      className={`inline-flex items-center gap-2 border px-2.5 py-1 text-[11px] ${bgCls}`}
      title={stale ? sync?.error : `Dernière synchro à ${absolute}`}
      data-testid="sync-badge"
    >
      <Icon className={`w-3 h-3 ${loading ? "animate-spin" : ""} ${colorCls}`} />
      <span className="font-mono text-zinc-300 whitespace-nowrap">
        {stale ? "⚠ Snapshot" : "Sync CHANTIX"} · {relative}
      </span>
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={loading}
          className="text-zinc-500 hover:text-white transition disabled:opacity-40"
          aria-label="Resynchroniser"
          data-testid="sync-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
        </button>
      )}
    </div>
  );
}
